import { loadMoov } from '@moovio/moov-js';
import axiosInstance from '../lib/axios-instance';
import { toast } from 'sonner';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TODO = any;

export type TRefundDetailsResponse = {
  method: string;
  refundData: TRefundData;
  isEconsentAgreed: boolean;
};

export enum ERefundType {
  REGULAR = 'regular',
  INSURANCE = 'insurance',
  BUSINESS = 'business',
}

export enum EEntityType {
  INDIVIDUAL = 'Individual',
  BUSINESS = 'Business',
  UNKNOWN = 'Unknown',
}

export type TRefundData = {
  email: string;
  facility_id: number;
  created_at: string;
  id: number;
  client_transaction_id: string;
  refund_to_fullname: string;
  first_name: string;
  refund_to_address_line1: string;
  refund_to_address_line2: string;
  refund_region: string;
  refund_guarantor_name: string;
  refund_to_city: string;
  refund_to_state: string;
  verified: TODO;
  refund_to_zip: string;
  last_name: string;
  subfacility_id: TODO;
  subfacility_name: TODO;
  undefined3: string;
  undefined4: string;
  undefined5: string;
  undefined6: string;
  organization_specific1: string;
  organization_specific2: string;
  organization_specific3: string;
  email_sent: string;
  email_sent_date: string;
  account_holder_name: TODO;
  routing_number: TODO;
  account_number: TODO;
  bank_name: TODO;
  payee_account_number: string;
  vendor_id_number: string;
  phone: TODO;
  refund_date: string;
  refund_amount: string;
  refund_status: string;
  email_sent_time: TODO;
  undefined1: string;
  undefined2: string;
  num_reminder_mail: TODO;
  stop_email: TODO;
  filename: string;
  email_subscription: boolean;
  refund_method: TODO;
  status_updatedat: string;
  refund_transaction_id: TODO;
  otp_created_at: string;
  otp_attempts: number;
  econsent: boolean;
  additional_info: TODO;
  status_updated_current: string;
  business_unit_identifier: string;
  business_unit_id: number;
  refund_to_country: TODO;
  refund_sub_status: string;
  transaction_id: TODO;
  updated_by: string;
  updated_at: string;
  is_error: boolean;
  is_deleted: boolean;
  refund_type: ERefundType;
  splitting_column: string;
  ach_method: string;
  facility_name: string;
  facility_logo: string;
  business_unit_logo: string;
  body: string;
  heading: string;
  allow_address_update: boolean;
  business_payee_name: string;
  files: {
    filePath: string;
    fileName: string;
  }[];
  entity_type: EEntityType;
  isCustomPaymentTypesEnabled: boolean;
};

type TGetLogosResponse = {
  facility_logo?: string;
  business_unit_logo?: string;
};

export type TEconsentResponse = {
  econsent?: Econsent;
  isEconsentAgreed: boolean;
};

export type Econsent = {
  id: number;
  facility_id: number;
  title: string;
  heading: string;
  body: string;
  logo: string;
  enabled: boolean;
};

export const getRefundDetails = async (): Promise<TRefundDetailsResponse> => {
  try {
    const res = await axiosInstance.get(`refund-details`);

    return res.data;
  } catch (e) {
    console.error(e);
    throw new Error('Failed to get direct payment details');
  }
};

export const agreeEconsent = async () => {
  try {
    const res = await axiosInstance.get(`agree-econsent`);

    return res.data;
  } catch (e) {
    console.error(e);
    throw new Error('Failed to agree econsent');
  }
};

export const refundeeEconsentDetails = async (): Promise<TEconsentResponse> => {
  try {
    const res = await axiosInstance.get(`refundee-econsent-details`);

    return res.data;
  } catch (e) {
    console.error(e);
    throw new Error('Failed to get Logos');
  }
};

export const getLogos = async (
  id: string | undefined,
): Promise<TGetLogosResponse> => {
  try {
    if (!id) return {};
    const res = await axiosInstance.get(`get-logos-refundee/${id}`);

    return res.data;
  } catch (e) {
    console.error(e);
    throw new Error('Failed to get Logos');
  }
};

export const postDefaultDirectPayment = async ({
  account_number,
  routing_number,
  bank_name,
}: {
  account_number: string;
  routing_number: string;
  bank_name: string;
}) => {
  try {
    const res = await axiosInstance.post(`update-refund-for-ach`, {
      account_number,
      routing_number,
      bank_name,
    });

    return res.data;
  } catch (e) {
    console.error(e);
    throw new Error('Failed to get direct payment details');
  }
};

export const moovDirectPayment = async ({
  holder_name,
  account_number,
  bank_account_type,
  routing_number,
  contactInfo,
  holder_type = 'individual',
}: {
  holder_name: string;
  account_number: string;
  bank_account_type: 'checking' | 'savings';
  routing_number: string;
  contactInfo: string;
  holder_type?: 'individual' | 'business';
}) => {
  try {
    const res = await axiosInstance.post(`create-moov-account-refundee`, {
      contactInfo,
    });

    const data = res.data;

    const moov = await loadMoov(data.token);
    let moovRes;
    try {
      moovRes = await moov?.accounts.bankAccounts.link({
        accountID: data?.accountID,
        bankAccount: {
          holderName: holder_name,
          holderType: holder_type,
          accountNumber: account_number,
          bankAccountType: bank_account_type,
          routingNumber: routing_number,
        },
      });
    } catch (error) {
      toast.error(
        'Failed to link bank account. Please check your bank details and try again.',
      );
      throw new Error('Failed to link bank account');
    }

    const transferRes = await axiosInstance.post(`create-transfer-refundee`, {
      toPaymentMethod: moovRes?.bankAccountID,
      transferType: 'ach',
    });

    return transferRes.data;
  } catch (e) {
    console.error(e);
    throw new Error('Failed to get direct payment details');
  }
};
